/**
 * useBillingCustomer Hook
 * =======================
 * 
 * Real-time subscription to billingCustomers collection.
 * Returns billing customer data for a given user.
 */

import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { BillingCustomerDoc } from '@/types/billing';

interface UseBillingCustomerResult {
  billingCustomer: BillingCustomerDoc | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Subscribe to billing customer data in real-time
 * 
 * @param userId - Firebase user ID (if omitted, must be provided by caller)
 */
export function useBillingCustomer(userId?: string): UseBillingCustomerResult {
  const [billingCustomer, setBillingCustomer] = useState<BillingCustomerDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const billingRef = doc(db, 'billingCustomers', userId);

    const unsubscribe = onSnapshot(
      billingRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setBillingCustomer(snapshot.data() as BillingCustomerDoc);
        } else {
          setBillingCustomer(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('[useBillingCustomer] Snapshot error:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  return { billingCustomer, loading, error };
}
